// API service for JWT authentication
import axios from 'axios'
import { removeAuthCookie, removeSessionCookie } from '@/lib/cookies'

// Get the API URL from environment variable or use a default
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
const GOOGLE_REDIRECT_URI = import.meta.env.VITE_GOOGLE_REDIRECT_URI || 'http://localhost:3000/auth/google/callback'

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// If any API call comes back 401 (expired/invalid session), clear all
// local auth state and force the user back to the SSO login page.
let isForcingLogout = false

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401 && !isForcingLogout) {
      isForcingLogout = true
      removeAuthToken()
      removeAuthCookie()
      removeSessionCookie()

      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth'
      }
    }
    return Promise.reject(error)
  },
);

// Types for authentication
export interface AuthPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
}

export interface OTPPayload {
  email: string;
  otp: string;
}

export interface ResendOTPPayload {
  email: string;
}

export interface PasswordResetRequestPayload {
  email: string;
}

export interface PasswordResetOTPPayload {
  email: string;
  otp_code: string;
  password: string;
  confirm_password: string;
}

export interface NoDataResponse {
  message: string;
  success: boolean;
}

export interface AuthData {
  token: string;
  pin_is_set: boolean;
  email_verified: boolean;
  otp_sent: boolean;
}

export interface AuthResponse {
  message: string;
  success: boolean;
  data: AuthData;
}

export interface GoogleAuthResponse {
  message: string;
  success: boolean;
  data: {
    url: string;
  }
}

export interface RegisterResponse {
  message: string;
  success: boolean;
  data: {
    id: number;
    email: string;
  }
}

// Store JWT token in localStorage
export const storeAuthToken = (token: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('jwt_token', token);
  }
};

// Get JWT token from localStorage
export const getAuthToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('jwt_token');
  }
  return null;
};

// Remove JWT token from localStorage
export const removeAuthToken = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('jwt_token');
  }
};

// Verify token with API
export const verifyToken = async (token: string): Promise<boolean> => {
  try {
    await api.get('/v2/auth/verify', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    return true;
  } catch (error) {
    return false;
  }
};

export interface VerifiedUserData {
  role: string;
  [key: string]: unknown;
}

// Verify token with API and return the user data (e.g. role) it carries.
// Resolves to null on any failure, including an expired/invalid session
// (a 401 here is also caught globally by the response interceptor above,
// which clears local auth state and redirects to /auth).
export const getVerifiedUser = async (token: string): Promise<VerifiedUserData | null> => {
  try {
    const response = await api.get('/v2/auth/verify', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data?.data ?? null;
  } catch {
    return null;
  }
};

// Login with email and password
export const loginWithEmailPassword = async (payload: AuthPayload): Promise<AuthData> => {
  try {
    const response = await api.post('/v2/auth/login', payload);
    const data: AuthData = response.data?.data;
    if (!data) throw new Error('Login failed');
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
    throw error;
  }
};

// Register with email and password
export const registerWithEmailPassword = async (payload: RegisterPayload): Promise<RegisterResponse> => {
  try {
    const response = await api.post('/v2/auth/register', payload);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
    throw new Error('Registration failed');
  }
};

// Exchange Google OAuth code for JWT
export const googleAuthCallback = async (token: string): Promise<AuthData> => {
  const deviceId = getOrCreateDeviceId()
  try {
    const response = await api.post('/v2/auth/google', {
      token,
      platform: 'web',
      device_id: deviceId,
    });
    const data: AuthData = response.data?.data;
    if (!data) throw new Error('Google authentication failed');
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Google authentication failed');
    }
    throw new Error('Google authentication failed');
  }
};

function getOrCreateDeviceId(): string {
  const key = 'hoomi_device_id'
  let id = localStorage.getItem(key)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(key, id)
  }
  return id
}

// Login with Google OAuth
export const loginWithGoogle = async (): Promise<GoogleAuthResponse> => {
  try {
    const response = await api.get(`/v2/auth/google?platform=web&redirect_base_url=${GOOGLE_REDIRECT_URI}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Google login failed');
    }
    throw new Error('Google login failed');
  }
};

// Verify OTP
export const verifyOTP = async (payload: OTPPayload): Promise<AuthResponse> => {
  try {
    const response = await api.post('/v2/auth/verify', payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'OTP verification failed');
    }
    throw new Error('OTP verification failed');
  }
};

// Resend OTP
export const resendOTP = async (payload: ResendOTPPayload): Promise<NoDataResponse> => {
  try {
    const response = await api.post('/v2/auth/resend-verification', payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Failed to resend OTP');
    }
    throw new Error('Failed to resend OTP');
  }
};

// Request password reset (sends OTP)
export const requestPasswordReset = async (payload: PasswordResetRequestPayload): Promise<NoDataResponse> => {
  try {
    const response = await api.post('/v2/auth/forgot-password', payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Failed to send password reset OTP');
    }
    throw new Error('Failed to send password reset OTP');
  }
};

// Reset password with token
export const resetPassword = async (payload: PasswordResetOTPPayload): Promise<NoDataResponse> => {
  try {
    const response = await api.post('/v2/auth/reset-password', payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
    throw new Error('Failed to reset password');
  }
};
